import {Component, Inject} from '@angular/core';
import {Localization} from '../../../../common/enums/localization';
import {ActionStatusType, ActionType, ApplicationType, UrgencyType} from "../../../../common/enums/enums";
import {EnumsService} from "../../../../common/services/enums.service";
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {ExecutorsService} from "../../../../executors/executors.service";
import {concatMap, finalize, map, switchMap, takeUntil, tap} from "rxjs/operators";
import {BuildingHelperService} from "../../../../common/services/building-helper.service";
import {Observable, of, Subject} from "rxjs";
import {TokenService} from "../../../../common/services/token.service";
import {BuildingJobsService} from "../../building-jobs.service";
import {Action} from "../../../../common/model/action";
import {BuildingIssuesService} from "../../building-issues.service";
import {Destroyable} from "../../../../../angular/component/destruction/destroyable.interface";
import {SelfDestruct} from "../../../../../angular/component/destruction/self-destruct";
import * as moment from 'moment';
import {IdAndLabel} from "../../../../common/model/id-and-label";
import {TranslateService} from "@ngx-translate/core";
import {Executor} from "../../../../executors/models/executor.model";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';


@Component({
  selector: 'app-job-modal',
  templateUrl: './job-modal.component.html',
  styleUrls: ['./job-modal.component.scss']
})
export class JobModalComponent implements Destroyable {
  public readonly destroyed$ = new Subject<this>();
  public readonly loading = {} as Loading;

  public readonly Localization = Localization;
  public readonly Urgency = UrgencyType;
  public readonly jobTypeOptions = this.enums.enumAsOptions(ActionType, 'actionType');

  public jobForm: UntypedFormGroup;
  public executorForm: UntypedFormGroup;
  public readonly issueForm = new UntypedFormGroup({
    'location': new UntypedFormControl({value: this.issue?.issueLocationPath, disabled: true}),
    'building': new UntypedFormControl({value: this.issue?.issueBuildingLabel, disabled: true}),
    'element': new UntypedFormControl({value: this.issue?.elementStructureLabel, disabled: true}),
  })
  public readonly executorOptions$ = this.buildingId$.pipe(
    switchMap((buildingId: string) => this.loadExecutors(buildingId)),
  );
  private readonly actionAdded = new Subject<Action>();
  public readonly actionAdded$ = this.actionAdded.pipe(takeUntil(this.destroyed$));

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: JobModalData,
    private readonly enums: EnumsService,
    private readonly buildingHelper: BuildingHelperService,
    private readonly executors: ExecutorsService,
    private readonly token: TokenService,
    private readonly issues: BuildingIssuesService,
    private readonly jobs: BuildingJobsService,
    private readonly self: MatDialogRef<JobModalComponent>,
    private readonly translate: TranslateService,
  ) {
    this.jobForm = this.hasMaintenanceProduct ? makeExtendedJobForm(this.data.action) : makeBasicJobForm(this.data.action);
    this.executorForm = makeExecutorForm();
  }

  public get issue() {
    return this.data.action?.issues?.[0];
  }

  public get culture() {
    return this.token.getCulture();
  }

  public get jobType() {
    return this.jobForm.controls.type.value || 'default';
  }

  public get canSave(): boolean {
    return this.jobForm.valid && this.jobForm.dirty;
  }

  public get canSend(): boolean {
    return this.statusIsOpen();
  }

  public get canChangeExecutor(): boolean {
    return this.statusIsOpen();
  }

  public get hasMaintenanceProduct(): boolean {
    return this.token.hasApplication(ApplicationType.MAINTENANCE);
  }

  public get isEditing(): boolean {
    return !!this.data.action?.id;
  }

  public get wantsNewExecutor(): boolean {
    return this.jobForm.controls.interventionTeam.value?.id === null;
  }

  public get canShowIssueDetails(): boolean {
    return !!this.issue?.issueLocationPath && !!this.issue.issueBuildingLabel;
  }

  private get buildingId$(): Observable<string> {
    const buildingId = this.data.buildingId || this.data.action.issueBuildingIds?.[0] || this.buildingHelper.getBuildingId();
    return of(buildingId);
  }

  public ngOnDestroy() {
    SelfDestruct(this);
  }

  public saveAndSend() {
    const saveAndSend = this.persist().pipe(
      concatMap((persistedJob) => this.sendJob(persistedJob))
    );
    this.exitModalAfter(
      this.indicateSavingDuring(saveAndSend)
    ).subscribe();
  }

  public save() {
    this.exitModalAfter(
      this.indicateSavingDuring(this.persist())
    ).subscribe();
  }

  public persist(): Observable<Action> {
    return this.saveExecutor().pipe(
      concatMap(() => this.saveAction()),
      finalize(() => this.loading.saving = false),
    );
  }

  cancel() {
    this.self.close();
  }

  private statusIsOpen() {
    return !this.data?.action?.status || ActionStatusType.OPEN == this.data?.action?.status;
  }

  private sendJob(job: Action) {
    return this.jobs.sendJob(job.id).pipe(map(() => {
      job.status = ActionStatusType.SENT;
      return job;
    }))
  }

  private saveExecutor(): Observable<Executor | null> {
    if (this.wantsNewExecutor) {
      return this.executors.addExecutor(this.formToExecutorModel())
        .pipe(tap((team) => this.jobForm.controls.interventionTeam.patchValue(team)));
    }
    return of(null);
  }

  private saveAction() {
    const action = this.formToActionModel();
    const persist = (this.isEditing ? this.jobs.update : this.jobs.add).bind(this.jobs) as PersistActionOperation;
    return persist(action)
      .pipe(
        tap(persistedAction => persistedAction.interventionTeam = this.jobForm.controls.interventionTeam.value),
      )
  }

  private formToActionModel() {
    const action = Object.entries(this.jobForm.controls)
      .reduce((action, [key, control]) => {
        let updatedValue = control.value || action[key];
        updatedValue = updatedValue instanceof Date ? moment(updatedValue).format() : updatedValue;
        action[key] = updatedValue;
        return action;
      }, {status: this.data.action?.status || ActionStatusType.OPEN, issueGuids: [this.data.issueGuid]} as Action);
    action.interventionTeamId = this.jobForm.controls.interventionTeam.value?.id;
    return action;
  }

  private formToExecutorModel(): Executor {
    const executor = new Executor();
    executor.label = this.executorForm.controls.label.value;
    executor.email = this.executorForm.controls.email.value;
    executor.contacts = [];
    return executor;
  }

  private loadExecutors(buildingId: string) {
    this.loading.executors = true;
    return this.executors.getAllFor(buildingId).pipe(
      map((executors: any) => executors?.items || []),
      map(executors => ([new IdAndLabel(null, '+ ' + this.translate.instant(Localization.AddNew)), ...executors])),
      finalize(() => this.loading.executors = false),
    );
  }

  private indicateSavingDuring<Type>(operation: Observable<Type>) {
    this.loading.saving = true;
    return operation.pipe(
      finalize(() => this.loading.saving = false)
    );
  }

  private exitModalAfter(operation: Observable<Action>) {
    return operation.pipe(
      tap(() => this.self.close()),
      tap((createdAction) => this.actionAdded.next(createdAction)),
    );
  }

}

function makeBasicJobForm(action: Action): UntypedFormGroup {
  const disabled = [ActionStatusType.FINISHED, ActionStatusType.CANCELLED].includes(action?.status);
  return new UntypedFormGroup({
    'id': new UntypedFormControl(action?.id),
    'type': new UntypedFormControl({value: action?.type, disabled}, [Validators.required]),
    'instructions': new UntypedFormControl({value: action?.instructions, disabled},),
    'interventionTeam': new UntypedFormControl({value: action?.interventionTeam, disabled}),
    'dueDate': new UntypedFormControl({value: (action?.dueDate ? moment(action.dueDate).toDate() : null), disabled}),
  });
}

function makeExtendedJobForm(action: Action) {
  const disabled = [ActionStatusType.FINISHED, ActionStatusType.CANCELLED].includes(action?.status);
  const base = makeBasicJobForm(action);
  return new UntypedFormGroup({
    ...base.controls,
    'urgency': new UntypedFormControl({value: action?.urgency, disabled}),
    'reference': new UntypedFormControl({value: action?.reference, disabled}),
    'workOrderReference': new UntypedFormControl({value: action?.workOrderReference, disabled}),
    'priority': new UntypedFormControl({value: action?.priority, disabled})
  })
}


function makeExecutorForm(): UntypedFormGroup {
  return new UntypedFormGroup({
    'label': new UntypedFormControl(null, [Validators.required]),
    'email': new UntypedFormControl(null, [Validators.required, Validators.email]),
  });
}

interface Loading {
  saving: boolean;
  executors: boolean,
}

export interface JobModalData {
  issueGuid: string,
  action?: Action,
  buildingId?: string,
}

type PersistActionOperation = (Action) => Observable<Action>;
